const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: What is an ODE? — slope field + solution curves ──
function DiagramSlopeField() {
  const W = 900, H = 340;
  // We'll draw a slope field for dy/dx = y (so solutions are Ce^x)
  // Grid from x=-3 to x=3, y=-2.5 to y=2.5
  const ox = 120, oy = 170, scx = 100, scy = 58;
  const tx = x => ox + x * scx;
  const ty = y => oy - y * scy;

  // Slope field ticks
  const ticks = [];
  for (let xi = -2; xi <= 4; xi += 0.6) {
    for (let yi = -2.5; yi <= 2.5; yi += 0.6) {
      const slope = yi; // dy/dx = y
      const len = 18;
      const angle = Math.atan(slope * scy / scx);
      const dx = (len / 2) * Math.cos(angle);
      const dy = (len / 2) * Math.sin(angle);
      const px = tx(xi), py = ty(yi);
      const clr = yi > 0 ? '#2E75B6' : yi < 0 ? '#c0392b' : '#888';
      ticks.push(<line key={`${xi},${yi}`} x1={px - dx} y1={py + dy} x2={px + dx} y2={py - dy}
        stroke={clr} strokeWidth="1.3" strokeOpacity="0.6" />);
    }
  }

  // Solution curves: y = C*e^x for C in {-1.5, -0.6, 0.3, 0.8, 1.5}
  const curves = [
    { C: 1.5, color: '#27ae60' },
    { C: 0.6, color: '#2E75B6' },
    { C: -0.6, color: '#c0392b' },
    { C: -1.5, color: '#9b59b6' },
  ];
  function solvePts(C) {
    const pts = [];
    for (let xi = -3; xi <= 3.05; xi += 0.08) {
      const y = C * Math.exp(xi);
      if (Math.abs(y) > 3.2) { if (pts.length) pts.push(null); continue; }
      pts.push([tx(xi), ty(y)]);
    }
    return pts;
  }
  function polyline(pts, color) {
    let d = '';
    let pen = false;
    for (const p of pts) {
      if (!p) { pen = false; continue; }
      d += (pen ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1) + ' ';
      pen = true;
    }
    return <path d={d} fill="none" stroke={color} strokeWidth="2.2" strokeOpacity="0.85" />;
  }

  // Right panel
  const px0 = 490, panW = W - px0 - 12;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Slope Field of dy/dx = y — Solutions are Exponentials y = Ce^x</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        {/* Axes */}
        <line x1={tx(-3)} y1={oy} x2={tx(3.8)} y2={oy} stroke="#1F3864" strokeWidth="1.6" />
        <line x1={ox} y1={ty(3)} x2={ox} y2={ty(-3)} stroke="#1F3864" strokeWidth="1.6" />
        <polygon points={`${tx(3.8)},${oy} ${tx(3.8)-8},${oy-4} ${tx(3.8)-8},${oy+4}`} fill="#1F3864" />
        <polygon points={`${ox},${ty(3)} ${ox-4},${ty(3)+8} ${ox+4},${ty(3)+8}`} fill="#1F3864" />
        <text x={tx(3.8)+6} y={oy+5} fontSize="15" fill="#1F3864" fontWeight="700">x</text>
        <text x={ox+5} y={ty(3)-4} fontSize="15" fill="#1F3864" fontWeight="700">y</text>
        {[-2,-1,1,2,3].map(v=>(
          <g key={v}>
            <text x={tx(v)} y={oy+16} textAnchor="middle" fontSize="11" fill="#bbb">{v}</text>
            <line x1={tx(v)} y1={oy-3} x2={tx(v)} y2={oy+3} stroke="#bbb" strokeWidth="1"/>
          </g>
        ))}
        {[-2,-1,1,2].map(v=>(
          <g key={v}>
            <text x={ox-6} y={ty(v)+4} textAnchor="end" fontSize="11" fill="#bbb">{v}</text>
            <line x1={ox-3} y1={ty(v)} x2={ox+3} y2={ty(v)} stroke="#bbb" strokeWidth="1"/>
          </g>
        ))}

        {/* Slope field */}
        {ticks}

        {/* Solution curves */}
        {curves.map(({ C, color }) => polyline(solvePts(C), color))}

        {/* Labels on curves */}
        {curves.map(({ C, color }) => {
          const xL = C > 0 ? 1.5 : -0.5;
          const yL = C * Math.exp(xL);
          if (Math.abs(yL) > 3) return null;
          return <text key={C} x={tx(xL)+6} y={ty(yL)-6} fontSize="12" fill={color} fontWeight="700">C={C}</text>;
        })}

        {/* Equilibrium */}
        <line x1={tx(-3)} y1={ty(0)} x2={tx(3.8)} y2={ty(0)} stroke="#888" strokeWidth="1.5" strokeDasharray="6,4" />
        <text x={tx(2.6)} y={ty(0)-8} fontSize="11" fill="#888" fontStyle="italic">y=0 (equilibrium)</text>

        {/* Right panel */}
        <rect x={px0} y={10} width={panW} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={px0 + panW / 2} y={34} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Reading a Slope Field</text>
        {[
          { y: 44, bg: '#EBF3FB', bc: '#2E75B6', lines: ['Each tick shows the slope dy/dx', 'at that (x, y) point.'] },
          { y: 112, bg: '#f0fff4', bc: '#27ae60', lines: ['Solution curves are tangent to', 'every tick they pass through.'] },
          { y: 180, bg: '#fff7ee', bc: '#e07b00', lines: ['Different C → different initial', 'condition y(0) = C.'] },
          { y: 248, bg: '#f5d0ff', bc: '#9b59b6', lines: ['Blue ticks: positive slope (y>0)', 'Red ticks: negative slope (y<0)'] },
        ].map(({ y, bg, bc, lines }) => (
          <g key={y}>
            <rect x={px0 + 6} y={y} width={panW - 12} height={60} rx={3} fill={bg} stroke={bc} strokeWidth="1.2" />
            <text x={px0 + 14} y={y + 20} fontSize="11" fill="#1F3864">{lines[0]}</text>
            <text x={px0 + 14} y={y + 38} fontSize="11" fill="#555">{lines[1]}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>A slope field visualises an ODE without solving it: at each point the tick shows the slope the solution must have there. Tracing a path tangent to every tick gives a solution curve. The family of all curves is parameterised by the constant C, determined by an initial condition.</div>
    </div>
  );
}

// ── Diagram 2: Classifying ODEs ──────────────────────────────────
function DiagramClassification() {
  const W = 900, H = 300;

  const boxes = [
    {
      x: 10, w: 170, color: '#2E75B6', bg: '#EBF3FB',
      title: 'ORDER', sub: 'highest derivative',
      items: ["1st order: y' = f(x,y)", "2nd order: y'' + y = 0", "nth order: y⁽ⁿ⁾ = ..."],
    },
    {
      x: 195, w: 175, color: '#27ae60', bg: '#f0fff4',
      title: 'LINEARITY', sub: 'in y and its derivs',
      items: ["Linear: a(x)y' + b(x)y = g(x)", "Non-linear: y·y' = x", "Key: coefficients in x only"],
    },
    {
      x: 385, w: 175, color: '#e07b00', bg: '#fff7ee',
      title: 'HOMOGENEITY', sub: 'of linear ODEs',
      items: ["Homogeneous: g(x)=0", "Non-homogeneous: g(x)≠0", "Soln = y_h + y_p"],
    },
    {
      x: 575, w: 175, color: '#c0392b', bg: '#fff0f0',
      title: 'SEPARABILITY', sub: 'for 1st order',
      items: ["dy/dx = f(x)·g(y)", "Separate: dy/g(y) = f(x)dx", "Integrate both sides"],
    },
    {
      x: 765, w: 125, color: '#9b59b6', bg: '#f5d0ff',
      title: 'AUTONOMY', sub: 'no explicit x',
      items: ["dy/dx = f(y)", "Equilibria: f(y)=0", "Phase portrait"],
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Taxonomy of Ordinary Differential Equations</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        {boxes.map(({ x, w, color, bg, title, sub, items }) => (
          <g key={title}>
            <rect x={x} y={8} width={w} height={H - 16} rx={6} fill={bg} stroke={color} strokeWidth="2" />
            <rect x={x} y={8} width={w} height={44} rx={6} fill={color} />
            <rect x={x} y={36} width={w} height={16} fill={color} />
            <text x={x + w / 2} y={30} textAnchor="middle" fontSize="13" fill="#fff" fontWeight="700">{title}</text>
            <text x={x + w / 2} y={64} textAnchor="middle" fontSize="10.5" fill={color} fontStyle="italic">{sub}</text>
            {items.map((t, i) => (
              <g key={i}>
                <rect x={x + 8} y={80 + i * 60} width={w - 16} height={52} rx={3} fill="#fff" fillOpacity="0.7" stroke={color} strokeWidth="0.8" />
                <text x={x + w / 2} y={80 + i * 60 + 20} textAnchor="middle" fontSize="10" fill="#1F3864" fontFamily="'Courier New',monospace" fontWeight="700">
                  {t.split(':')[0] + (t.includes(':') ? ':' : '')}
                </text>
                <text x={x + w / 2} y={80 + i * 60 + 38} textAnchor="middle" fontSize="10" fill="#555">
                  {t.includes(':') ? t.split(':').slice(1).join(':').trim() : ''}
                </text>
              </g>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Every ODE can be described along these five axes. The classification drives the solution strategy: separable equations are solved by integration, linear first-order equations by integrating factors, linear second-order homogeneous equations by the characteristic equation, and so on.</div>
    </div>
  );
}

// ── Diagram 3: Integrating Factor Method ────────────────────────
function DiagramIntegratingFactor() {
  const W = 900, H = 320;

  // Draw a step-by-step flow with boxes and arrows
  const steps = [
    { x: 10,  label: 'Standard Form', eq: "dy/dx + P(x)y = Q(x)", color: '#2E75B6', bg: '#EBF3FB' },
    { x: 200, label: 'Integrating Factor', eq: "μ(x) = e^(∫P(x)dx)", color: '#27ae60', bg: '#f0fff4' },
    { x: 390, label: 'Multiply Through', eq: "d/dx[μy] = μQ(x)", color: '#e07b00', bg: '#fff7ee' },
    { x: 580, label: 'Integrate Both Sides', eq: "μy = ∫μQ(x)dx + C", color: '#c0392b', bg: '#fff0f0' },
    { x: 755, label: 'Solve for y', eq: "y = [∫μQ dx + C] / μ", color: '#9b59b6', bg: '#f5d0ff' },
  ];

  // Example: dy/dx + (2/x)y = x²  →  y = x³/5 + C/x²
  // solution curve points
  function solPts(C) {
    const pts = [];
    for (let x = 0.3; x <= 3.8; x += 0.07) {
      const y = x * x * x / 5 + C / (x * x);
      if (Math.abs(y) > 4) { pts.push(null); continue; }
      pts.push([x, y]);
    }
    return pts;
  }
  const colors2 = ['#2E75B6', '#27ae60', '#e07b00', '#9b59b6'];
  const Cvals = [0.3, 0.8, -0.3, -0.8];

  const gx = 0, gy = 195, gW = 370, gH = 120;
  const ox2 = gx + 50, oy2 = gy + gH - 20;
  const scx2 = 72, scy2 = 22;
  const tx2 = x => ox2 + (x - 0.5) * scx2;
  const ty2 = y => oy2 - y * scy2;

  function polyline2(pts, color) {
    let d = '';
    let pen = false;
    for (const p of pts) {
      if (!p) { pen = false; continue; }
      const px = tx2(p[0]), py = ty2(p[1]);
      if (py < gy || py > gy + gH + 5) { pen = false; continue; }
      d += (pen ? 'L' : 'M') + px.toFixed(1) + ' ' + py.toFixed(1) + ' ';
      pen = true;
    }
    return <path d={d} fill="none" stroke={color} strokeWidth="2" />;
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Integrating Factor — First-Order Linear ODE Method</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Step boxes */}
        {steps.map(({ x, label, eq, color, bg }, i) => (
          <g key={i}>
            <rect x={x} y={10} width={172} height={120} rx={6} fill={bg} stroke={color} strokeWidth="2" />
            <rect x={x} y={10} width={172} height={32} rx={6} fill={color} />
            <rect x={x} y={30} width={172} height={12} fill={color} />
            <text x={x + 86} y={28} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{`STEP ${i + 1}: ${label}`}</text>
            <text x={x + 86} y={68} textAnchor="middle" fontSize="12.5" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{eq.split(' = ')[0]}</text>
            <text x={x + 86} y={88} textAnchor="middle" fontSize="11" fill="#555">= {eq.split(' = ')[1]}</text>
            {i < steps.length - 1 && (
              <polygon points={`${x + 178},65 ${x + 192},70 ${x + 178},75`} fill={color} />
            )}
          </g>
        ))}

        {/* Example working */}
        <rect x={0} y={148} width={W} height={1} fill="#ccd9ea" />
        <text x={10} y={172} fontSize="13" fill="#1F3864" fontWeight="700">Worked Example: </text>
        <text x={140} y={172} fontSize="13" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">dy/dx + (2/x)y = x²</text>
        <text x={380} y={172} fontSize="12" fill="#555">→  P(x)=2/x, Q(x)=x²  →  μ=e^(∫2/x dx)=x²</text>
        <text x={380} y={192} fontSize="12" fill="#555">→  d/dx[x²y] = x⁴  →  x²y = x⁵/5 + C  →  y = x³/5 + C/x²</text>

        {/* Mini graph of solutions */}
        <rect x={gx} y={gy} width={gW} height={gH} rx={4} fill="#fff" stroke="#ccd9ea" />
        <text x={gx + gW / 2} y={gy - 5} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Solution family y = x³/5 + C/x²</text>
        <line x1={ox2} y1={ty2(-1)} x2={ox2} y2={ty2(5.5)} stroke="#1F3864" strokeWidth="1" />
        <line x1={tx2(0.5)} y1={oy2} x2={tx2(3.9)} y2={oy2} stroke="#1F3864" strokeWidth="1" />
        <text x={tx2(3.9) + 4} y={oy2 + 4} fontSize="10" fill="#1F3864">x</text>
        <text x={ox2 + 2} y={ty2(5.5) - 2} fontSize="10" fill="#1F3864">y</text>
        {[1, 2, 3].map(v => (
          <g key={v}>
            <text x={tx2(v)} y={oy2 + 12} textAnchor="middle" fontSize="9" fill="#bbb">{v}</text>
          </g>
        ))}
        {Cvals.map((C, i) => polyline2(solPts(C), colors2[i]))}
        {Cvals.map((C, i) => {
          const xL = 3.2;
          const y = xL * xL * xL / 5 + C / (xL * xL);
          const px = tx2(xL), py = ty2(y);
          if (py < gy || py > gy + gH) return null;
          return <text key={C} x={px + 4} y={py + 4} fontSize="9" fill={colors2[i]} fontWeight="700">C={C}</text>;
        })}

        {/* Explanation boxes */}
        {[
          { x: 385, y: 210, w: 240, bg: '#EBF3FB', bc: '#2E75B6', t1: 'Why multiply by μ?', t2: 'Left side becomes d/dx[μy] — a perfect derivative → easy to integrate.' },
          { x: 640, y: 210, w: 250, bg: '#f0fff4', bc: '#27ae60', t1: 'The constant C', t2: 'Each value of C gives one solution. C is fixed by an initial condition e.g. y(1)=2.' },
        ].map(({ x, y, w, bg, bc, t1, t2 }) => (
          <g key={t1}>
            <rect x={x} y={y} width={w} height={95} rx={4} fill={bg} stroke={bc} strokeWidth="1.5" />
            <text x={x + 10} y={y + 20} fontSize="11.5" fill="#1F3864" fontWeight="700">{t1}</text>
            <foreignObject x={x + 10} y={y + 28} width={w - 20} height={65}>
              <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontSize: '10.5px', color: '#444', lineHeight: '1.5' }}>{t2}</div>
            </foreignObject>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The integrating factor μ(x)=e^(∫P dx) is chosen so that multiplying the ODE turns the left side into an exact derivative. This is the standard method for all first-order linear ODEs — linear means y and y′ appear to the first power only, with coefficients depending on x.</div>
    </div>
  );
}

// ── Diagram 4: Characteristic Equation (2nd order) ──────────────
function DiagramCharacteristic() {
  const W = 900, H = 330;

  // Three cases based on discriminant
  const cases = [
    {
      x: 8, w: 278, color: '#2E75B6', bg: '#EBF3FB',
      disc: 'Δ = b²−4ac > 0', title: 'Two Real Roots r₁ ≠ r₂',
      sol: 'y = C₁e^(r₁x) + C₂e^(r₂x)',
      example: 'y″−5y′+6y=0  →  r²−5r+6=0',
      roots: '→ r=2, r=3',
      pts: (() => {
        // e^2x + e^3x type
        const arr = [];
        for (let x = -1; x <= 1.4; x += 0.06) {
          arr.push([x, 0.6 * Math.exp(2 * x) + 0.4 * Math.exp(3 * x)]);
        }
        return arr;
      })(),
    },
    {
      x: 304, w: 278, color: '#27ae60', bg: '#f0fff4',
      disc: 'Δ = b²−4ac = 0', title: 'Repeated Root r₁ = r₂ = r',
      sol: 'y = (C₁ + C₂x)e^(rx)',
      example: 'y″−4y′+4y=0  →  r²−4r+4=0',
      roots: '→ r=2 (double)',
      pts: (() => {
        const arr = [];
        for (let x = -1; x <= 1.4; x += 0.06) {
          arr.push([x, (0.5 + 0.4 * x) * Math.exp(2 * x)]);
        }
        return arr;
      })(),
    },
    {
      x: 600, w: 292, color: '#c0392b', bg: '#fff0f0',
      disc: 'Δ = b²−4ac < 0', title: 'Complex Roots α ± βi',
      sol: 'y = e^(αx)(C₁cos βx + C₂sin βx)',
      example: 'y″+4y=0  →  r²+4=0',
      roots: '→ r=±2i (α=0, β=2)',
      pts: (() => {
        const arr = [];
        for (let x = -1; x <= 1.4; x += 0.06) {
          arr.push([x, Math.cos(2 * x * Math.PI)]);
        }
        return arr;
      })(),
    },
  ];

  const gH = 80, gY = 200;

  function miniGraph(pts, x0, w, color) {
    const gx = x0 + 10, gw = w - 20;
    const midY = gY + gH / 2;
    const scx = gw / 2.5;
    const scy = gH / 3.5;
    let d = '';
    let pen = false;
    for (const [xi, yi] of pts) {
      const px = gx + (xi + 1) * scx;
      const py = midY - yi * scy;
      if (py < gY - 5 || py > gY + gH + 5) { pen = false; continue; }
      d += (pen ? 'L' : 'M') + px.toFixed(1) + ' ' + py.toFixed(1) + ' ';
      pen = true;
    }
    return (
      <g>
        <rect x={gx} y={gY} width={gw} height={gH} rx={3} fill="#fff" stroke="#ddd" />
        <line x1={gx} y1={midY} x2={gx + gw} y2={midY} stroke="#bbb" strokeWidth="0.8" />
        <path d={d} fill="none" stroke={color} strokeWidth="2" />
      </g>
    );
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Second-Order Linear ODE: ay″ + by′ + cy = 0 — Characteristic Equation ar² + br + c = 0</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        {cases.map(({ x, w, color, bg, disc, title, sol, example, roots, pts }) => (
          <g key={title}>
            <rect x={x} y={8} width={w} height={185} rx={6} fill={bg} stroke={color} strokeWidth="2" />
            <rect x={x} y={8} width={w} height={36} rx={6} fill={color} />
            <rect x={x} y={32} width={w} height={12} fill={color} />
            <text x={x + w / 2} y={24} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700" fontFamily="'Courier New',monospace">{disc}</text>
            <text x={x + w / 2} y={60} textAnchor="middle" fontSize="11.5" fill={color} fontWeight="700">{title}</text>
            <rect x={x + 8} y={72} width={w - 16} height={30} rx={3} fill="#fff" fillOpacity="0.8" />
            <text x={x + w / 2} y={91} textAnchor="middle" fontSize="12" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{sol}</text>
            <text x={x + 10} y={125} fontSize="10.5" fill="#555">{example}</text>
            <text x={x + 10} y={142} fontSize="10.5" fill="#1F3864" fontWeight="700">{roots}</text>
            <text x={x + w / 2} y={175} textAnchor="middle" fontSize="10" fill="#888" fontStyle="italic">Solution shape →</text>
            {miniGraph(pts, x, w, color)}
          </g>
        ))}
        <text x={W / 2} y={295} textAnchor="middle" fontSize="11" fill="#555" fontStyle="italic">
          Mini graphs show typical solution shape for each case (not to scale)
        </text>
      </svg>
      <div style={S.dCaption}>For ay″+by′+cy=0, substitute y=e^(rx) to get the characteristic equation ar²+br+c=0. The discriminant Δ=b²−4ac governs whether solutions are real exponentials (Δ&gt;0), a degenerate exponential-polynomial (Δ=0), or damped oscillations (Δ&lt;0). Initial conditions y(0) and y′(0) determine C₁ and C₂.</div>
    </div>
  );
}

// ── Diagram 5: Euler's Method ────────────────────────────────────
function DiagramEuler() {
  const W = 900, H = 320;

  // dy/dx = x + y/2, exact solution through (0,0.5): y = -2x - 4 + 4.5*e^(x/2)
  // Euler from (0, 0.5) with h=0.5 and h=0.25
  const exact = x => -2 * x - 4 + 4.5 * Math.exp(x / 2);
  const f = (x, y) => x + y / 2;

  function eulerPts(x0, y0, h, steps) {
    const pts = [[x0, y0]];
    let x = x0, y = y0;
    for (let i = 0; i < steps; i++) {
      y = y + h * f(x, y);
      x = x + h;
      pts.push([x, y]);
    }
    return pts;
  }

  const eu05 = eulerPts(0, 0.5, 0.5, 6);
  const eu025 = eulerPts(0, 0.5, 0.25, 12);

  const ox = 60, oy = 200, scx = 100, scy = 55;
  const tx = x => ox + x * scx;
  const ty = y => oy - y * scy;

  function exactPath() {
    let d = '';
    let pen = false;
    for (let x = 0; x <= 3.2; x += 0.05) {
      const y = exact(x);
      if (y > 4 || y < -1) { pen = false; continue; }
      d += (pen ? 'L' : 'M') + tx(x).toFixed(1) + ' ' + ty(y).toFixed(1) + ' ';
      pen = true;
    }
    return d;
  }

  function eulerPath(pts) {
    return pts.map((p, i) => (i === 0 ? `M${tx(p[0]).toFixed(1)} ${ty(p[1]).toFixed(1)}` : `L${tx(p[0]).toFixed(1)} ${ty(p[1]).toFixed(1)}`)).join(' ');
  }

  const px0 = 380, panW = W - px0 - 10;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Euler's Method — Numerical Approximation of dy/dx = x + y/2, y(0)=0.5</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        {/* Grid lines */}
        {[0,1,2,3].map(v => (
          <g key={v}>
            <line x1={tx(v)} y1={ty(-0.5)} x2={tx(v)} y2={ty(4)} stroke="#eee" strokeWidth="1" />
            <line x1={tx(0)} y1={ty(v)} x2={tx(3.2)} y2={ty(v)} stroke="#eee" strokeWidth="1" />
            <text x={tx(v)} y={oy + 16} textAnchor="middle" fontSize="11" fill="#bbb">{v}</text>
            {v > 0 && <text x={ox - 6} y={ty(v) + 4} textAnchor="end" fontSize="11" fill="#bbb">{v}</text>}
          </g>
        ))}
        {/* Axes */}
        <line x1={tx(0)} y1={ty(-0.5)} x2={tx(0)} y2={ty(4)} stroke="#1F3864" strokeWidth="1.6" />
        <line x1={tx(0)} y1={oy} x2={tx(3.2)} y2={oy} stroke="#1F3864" strokeWidth="1.6" />
        <polygon points={`${tx(3.2)},${oy} ${tx(3.2)-8},${oy-4} ${tx(3.2)-8},${oy+4}`} fill="#1F3864" />
        <polygon points={`${tx(0)},${ty(4)} ${tx(0)-4},${ty(4)+8} ${tx(0)+4},${ty(4)+8}`} fill="#1F3864" />
        <text x={tx(3.2) + 6} y={oy + 4} fontSize="14" fill="#1F3864" fontWeight="700">x</text>
        <text x={tx(0) + 5} y={ty(4) - 4} fontSize="14" fill="#1F3864" fontWeight="700">y</text>

        {/* Exact solution */}
        <path d={exactPath()} fill="none" stroke="#1F3864" strokeWidth="2.5" />
        <text x={tx(2.2) + 6} y={ty(exact(2.2)) - 8} fontSize="12" fill="#1F3864" fontWeight="700">Exact</text>

        {/* Euler h=0.5 */}
        <path d={eulerPath(eu05)} fill="none" stroke="#c0392b" strokeWidth="2" strokeDasharray="8,4" />
        {eu05.map(([x, y], i) => (
          <circle key={i} cx={tx(x)} cy={ty(y)} r={4} fill="#c0392b" stroke="#fff" strokeWidth="1.5" />
        ))}
        <text x={tx(eu05[4][0]) + 8} y={ty(eu05[4][1])} fontSize="11" fill="#c0392b" fontWeight="700">h=0.5</text>

        {/* Euler h=0.25 */}
        <path d={eulerPath(eu025)} fill="none" stroke="#e07b00" strokeWidth="1.8" strokeDasharray="5,3" />
        {eu025.map(([x, y], i) => (
          i % 2 === 0 && <circle key={i} cx={tx(x)} cy={ty(y)} r={3} fill="#e07b00" stroke="#fff" strokeWidth="1.2" />
        ))}
        <text x={tx(eu025[8][0]) + 6} y={ty(eu025[8][1]) - 10} fontSize="11" fill="#e07b00" fontWeight="700">h=0.25</text>

        {/* Right panel */}
        <rect x={px0} y={8} width={panW} height={H - 16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={px0 + panW / 2} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Euler Algorithm</text>
        {[
          { y: 38, bg: '#EBF3FB', bc: '#2E75B6', title: 'Formula', lines: ["xₙ₊₁ = xₙ + h", "yₙ₊₁ = yₙ + h·f(xₙ,yₙ)"] },
          { y: 118, bg: '#fff7ee', bc: '#e07b00', title: 'Error', lines: ["Local error: O(h²)", "Global error: O(h)"] },
          { y: 198, bg: '#f0fff4', bc: '#27ae60', title: 'Improvements', lines: ["Runge-Kutta (RK4)", "uses slope estimates"] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={px0 + 6} y={y} width={panW - 12} height={72} rx={3} fill={bg} stroke={bc} strokeWidth="1.2" />
            <text x={px0 + 14} y={y + 18} fontSize="11" fill="#1F3864" fontWeight="700">{title}</text>
            <text x={px0 + 14} y={y + 38} fontSize="11" fill="#555" fontFamily="'Courier New',monospace">{lines[0]}</text>
            <text x={px0 + 14} y={y + 56} fontSize="11" fill="#555" fontFamily="'Courier New',monospace">{lines[1]}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Euler's method marches forward: from the current point, take a step of size h in the direction given by the ODE's slope. Smaller h gives better accuracy but more computation. Red (h=0.5) drifts noticeably from the exact solution; orange (h=0.25) tracks it more closely. Runge-Kutta methods achieve much higher accuracy at the same step size.</div>
    </div>
  );
}

// ── Diagram 6: Solution Methods Overview ────────────────────────
function DiagramMethodsMap() {
  const W = 900, H = 280;

  const methods = [
    { x: 8,   w: 172, color: '#2E75B6', bg: '#EBF3FB', title: 'Separation of Variables', when: '1st order, separable', how: 'dy/g(y) = f(x)dx\nIntegrate both sides', ex: 'dy/dx = xy → ln|y|=x²/2+C' },
    { x: 188, w: 172, color: '#27ae60', bg: '#f0fff4', title: 'Integrating Factor', when: '1st order, linear', how: "μ=e^(∫P dx)\nMultiply, then integrate", ex: "y'+Py=Q → (μy)'=μQ" },
    { x: 368, w: 172, color: '#e07b00', bg: '#fff7ee', title: 'Characteristic Eqn', when: '2nd order, linear, const coeff', how: 'ar²+br+c=0\nFind r, write y', ex: "y''+y=0 → r=±i → cos,sin" },
    { x: 548, w: 172, color: '#c0392b', bg: '#fff0f0', title: 'Variation of Parameters', when: '2nd order, linear, non-homog.', how: 'y=u₁y₁+u₂y₂, find u₁,u₂\nusing Wronskian', ex: "y_p found when g(x) tricky" },
    { x: 728, w: 163, color: '#9b59b6', bg: '#f5d0ff', title: 'Undetermined Coefficients', when: '2nd order, const coeff, g(x) nice', how: 'Guess form of y_p\nMatch to g(x)', ex: "g=e^x → try y_p=Ae^x" },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Solution Method Selection — Which Technique for Which ODE?</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        {methods.map(({ x, w, color, bg, title, when, how, ex }) => {
          const lines = how.split('\n');
          return (
            <g key={title}>
              <rect x={x} y={8} width={w} height={H - 16} rx={6} fill={bg} stroke={color} strokeWidth="2" />
              <rect x={x} y={8} width={w} height={38} rx={6} fill={color} />
              <rect x={x} y={34} width={w} height={12} fill={color} />
              <text x={x + w / 2} y={24} textAnchor="middle" fontSize="10.5" fill="#fff" fontWeight="700">{title.split(' ').slice(0,2).join(' ')}</text>
              <text x={x + w / 2} y={38} textAnchor="middle" fontSize="10" fill="#fff" fontWeight="700">{title.split(' ').slice(2).join(' ')}</text>
              <text x={x + 8} y={70} fontSize="10" fill="#555" fontWeight="700">When:</text>
              <text x={x + 8} y={84} fontSize="9.5" fill="#333">{when}</text>
              <line x1={x + 8} y1={96} x2={x + w - 8} y2={96} stroke={color} strokeWidth="0.8" />
              <text x={x + 8} y={112} fontSize="10" fill="#555" fontWeight="700">How:</text>
              <text x={x + 8} y={126} fontSize="9.5" fill="#333" fontFamily="'Courier New',monospace">{lines[0]}</text>
              <text x={x + 8} y={142} fontSize="9.5" fill="#333">{lines[1]}</text>
              <line x1={x + 8} y1={154} x2={x + w - 8} y2={154} stroke={color} strokeWidth="0.8" />
              <text x={x + 8} y={170} fontSize="10" fill="#555" fontWeight="700">Example:</text>
              <text x={x + 8} y={186} fontSize="9" fill={color} fontFamily="'Courier New',monospace">{ex.split(' → ')[0]}</text>
              {ex.includes('→') && <text x={x + 8} y={202} fontSize="9" fill="#333">{ex.split(' → ')[1]}</text>}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>Choosing the right technique is half the battle. First-order equations are handled by separation or integrating factors. Constant-coefficient second-order equations use the characteristic equation for the homogeneous part, and either undetermined coefficients or variation of parameters for the particular solution. Numerical methods (Euler, RK4) handle cases without closed-form solutions.</div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────
export default function IntroODEs() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Introduction to Ordinary Differential Equations</h1>
      <p style={S.p}>An ordinary differential equation (ODE) is an equation relating a function y(x) to one or more of its derivatives. Rather than finding a number, we find a function — or an entire family of functions — satisfying the equation. ODEs are the mathematical language of change: they underpin Newton's laws, electrical circuits, population dynamics, chemical kinetics, and the motion of every satellite ever launched.</p>

      <InfoBox title="Core Idea: An ODE is an equation whose unknown is a function" items={[
        "An ODE involves y(x) and its derivatives y′, y″, … (all ordinary, not partial)",
        "The general solution contains arbitrary constants C₁, C₂, … (one per order)",
        "A particular solution fixes the constants via initial/boundary conditions",
        "Order = highest derivative present; degree = power of that highest derivative",
        "Linear ODE: y and its derivatives appear only to the first power — superposition applies",
        "Non-linear ODEs are harder; analytic solutions often don't exist",
      ]} />

      <h2 style={S.h2}>Slope Fields and Solution Curves</h2>
      <DiagramSlopeField />
      <Table2 rows={[
        ["ODE definition", "An equation F(x, y, y′, y″, …, y⁽ⁿ⁾) = 0\nRelates the unknown function y and its derivatives"],
        ["Slope field", "At each (x,y) draw a short tick of slope = dy/dx given by the ODE\nAllows geometric understanding without solving"],
        ["Solution curve", "A curve y=f(x) tangent to every slope field tick it crosses\nEach curve is one particular solution"],
        ["General solution", "Family of all solutions, parameterised by constant(s) C\ne.g. dy/dx=y → y=Ce^x for any constant C"],
        ["Particular solution", "One specific solution satisfying y(x₀)=y₀\nFixes C: if y(0)=3 then C=3"],
        ["Equilibrium solution", "Constant solution where dy/dx=0 for all x\ne.g. y=0 is an equilibrium of dy/dx=y"],
      ]} />

      <h2 style={S.h2}>Classifying ODEs</h2>
      <DiagramClassification />
      <Table2 rows={[
        ["Order", "Highest derivative in the equation\n1st order: y′=f(x,y); 2nd order: y″+p(x)y′+q(x)y=g(x)"],
        ["Linear", "y and its derivatives appear only to the first power; coefficients depend on x only\na_n(x)y⁽ⁿ⁾+…+a₁(x)y′+a₀(x)y = g(x)"],
        ["Non-linear", "Any product, power, or transcendental function of y or its derivatives\ne.g. y·y′=x, (y″)²=y, sin(y)=x"],
        ["Homogeneous (linear)", "g(x)=0: all terms involve y\nSolutions form a vector space; general solution = Σ Cᵢyᵢ"],
        ["Non-homogeneous (linear)", "g(x)≠0\nGeneral solution = y_h (homogeneous) + y_p (particular)"],
        ["Separable", "dy/dx = f(x)·g(y): variables can be separated to opposite sides\nIntegrate ∫dy/g(y) = ∫f(x)dx"],
        ["Autonomous", "dy/dx = f(y): right-hand side depends only on y, not x\nEquilibria at f(y)=0; analysed via phase line"],
      ]} />

      <h2 style={S.h2}>Separable Equations & Integrating Factor</h2>
      <DiagramIntegratingFactor />
      <Table2 rows={[
        ["Separable method", "Rewrite as dy/g(y) = f(x)dx, then ∫dy/g(y) = ∫f(x)dx + C\nWorks whenever the ODE factors into a function of y times a function of x"],
        ["Example (separable)", "dy/dx = 2xy  →  dy/y = 2x dx  →  ln|y| = x²+C  →  y = Ae^(x²)"],
        ["1st order linear form", "dy/dx + P(x)y = Q(x)"],
        ["Integrating factor μ", "μ(x) = e^(∫P(x)dx)\nChosen so d/dx[μy] = μQ — the left side becomes a perfect derivative"],
        ["General solution", "y = (1/μ)·[∫μQ dx + C]"],
        ["Example (lin. 1st order)", "y′ + (2/x)y = x²  →  μ = x²  →  d/dx[x²y] = x⁴  →  y = x³/5 + C/x²"],
        ["Bernoulli equation", "y′ + P(x)y = Q(x)yⁿ: substitute v=y^(1−n) to linearise"],
        ["Exact equations", "M dx + N dy = 0 is exact if ∂M/∂y = ∂N/∂x\nSolution F(x,y)=C where ∂F/∂x=M, ∂F/∂y=N"],
      ]} />

      <h2 style={S.h2}>Second-Order Linear ODEs — Characteristic Equation</h2>
      <DiagramCharacteristic />
      <Table2 rows={[
        ["Form", "ay″ + by′ + cy = 0  (constant coefficients, homogeneous)\nSubstitute y=e^(rx) → characteristic equation ar²+br+c=0"],
        ["Two distinct real roots", "r₁ ≠ r₂:  y = C₁e^(r₁x) + C₂e^(r₂x)\nExponential growth/decay depending on signs of rᵢ"],
        ["Repeated real root", "r₁ = r₂ = r:  y = (C₁ + C₂x)e^(rx)\nThe extra factor x avoids linear dependence"],
        ["Complex roots α±βi", "y = e^(αx)(C₁cos βx + C₂sin βx)\nOscillatory; α<0 gives damped oscillations, α=0 pure oscillation"],
        ["Wronskian", "W(y₁,y₂) = y₁y₂′ − y₂y₁′\nW≠0 confirms y₁,y₂ are linearly independent — a valid basis"],
        ["Non-homogeneous: y_p", "Undetermined coefficients: guess form of y_p matching g(x)\nVariation of parameters: y_p = −y₁∫(y₂g/W)dx + y₂∫(y₁g/W)dx"],
        ["Undetermined coefficients", "g(x) = polynomial → try y_p = polynomial of same degree\ng(x) = e^(αx) → try Ae^(αx); g = sin/cos → try A cos+B sin"],
        ["Initial value problem", "Specify y(x₀) and y′(x₀) to fix C₁ and C₂ uniquely"],
      ]} />

      <h2 style={S.h2}>Numerical Methods — Euler and Runge-Kutta</h2>
      <DiagramEuler />
      <Table2 rows={[
        ["Euler's method", "yₙ₊₁ = yₙ + h·f(xₙ,yₙ)\nSimplest numerical scheme; local error O(h²), global error O(h)"],
        ["Step size h", "Smaller h → more accurate but more work\nToo large: may become unstable or diverge"],
        ["Midpoint method", "yₙ₊₁ = yₙ + h·f(xₙ + h/2, yₙ + h/2·f(xₙ,yₙ))\n2nd-order method: global error O(h²)"],
        ["RK4 (Runge-Kutta 4)", "k₁=hf(xₙ,yₙ), k₂=hf(xₙ+h/2,yₙ+k₁/2),\nk₃=hf(xₙ+h/2,yₙ+k₂/2), k₄=hf(xₙ+h,yₙ+k₃)\nyₙ₊₁=yₙ+(k₁+2k₂+2k₃+k₄)/6; global error O(h⁴)"],
        ["Stability", "Some ODEs require small h for stability (stiff ODEs)\nImplicit methods (e.g. backward Euler) handle stiffness better"],
        ["When numerical?", "No closed-form solution exists, or g(x) is given as data\ne.g. weather models, rocket trajectories"],
      ]} />

      <h2 style={S.h2}>Solution Method Selection</h2>
      <DiagramMethodsMap />
      <Table2 rows={[
        ["Identify the type first", "Is it linear? What order? Constant or variable coefficients? Is g(x)=0?\nAns to these questions determines the strategy"],
        ["Separation", "1st order, separable: dy/g(y) = f(x)dx then integrate\nCovers: exponential growth/decay, Newton's cooling, logistic"],
        ["Integrating factor", "1st order, linear y′+P(x)y=Q(x)\nμ=e^(∫P dx), then integrate d/dx[μy]=μQ"],
        ["Characteristic equation", "2nd order, linear, constant coefficients, homogeneous\nar²+br+c=0, three cases by discriminant"],
        ["Undetermined coefficients", "2nd order, const coeff, non-homogeneous, g(x)=polynomial/exp/trig\nGuess y_p form, substitute, match coefficients"],
        ["Variation of parameters", "2nd order, linear, non-homogeneous, any g(x)\nMore general than undetermined coefficients; needs y_h first"],
        ["Numerical (Euler, RK4)", "Any ODE without closed-form solution, or given as data\nChoose h small enough for accuracy and stability"],
      ]} />

      <InfoBox title="Key Takeaway — ODE Problem-Solving Checklist" items={[
        "What order? → 1st order: separation or integrating factor; 2nd order: characteristic equation",
        "Is it linear? → Non-linear usually needs separation or numerical methods",
        "Homogeneous? → y_h by characteristic roots; then add y_p for non-homogeneous",
        "Characteristic discriminant Δ=b²−4ac: >0 real exponentials, =0 poly×exp, <0 damped oscillation",
        "Initial conditions given? → substitute x₀,y₀ (and y′(x₀) for 2nd order) to find C₁, C₂",
        "No analytic solution? → Euler's method (O(h) error) or RK4 (O(h⁴) error)",
        "Slope field: visualise without solving; equilibria are horizontal solutions where f(y)=0",
        "Always check by differentiating your answer and substituting back into the ODE",
      ]} />
    </div>
  );
}